import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDate } from '../utils/dateFormat'
import { InventoryReturn, InventoryReturnItem, InventoryItem, Warehouse, InventoryConsumption, ExpenseArticle, Tournament } from '../types'
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api'

const TOURNAMENT_STATUS_PRIORITY: Record<string, number> = {
  ACTIVE: 0,
  PLANNED: 1,
  COMPLETED: 2,
  CANCELLED: 3
}

const TOURNAMENT_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Активные',
  PLANNED: 'Запланированные',
  COMPLETED: 'Завершенные',
  CANCELLED: 'Отмененные'
}

const InventoryReturns: React.FC = () => {
  const navigate = useNavigate()
  const [returns, setReturns] = useState<InventoryReturn[]>([])
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingReturn, setEditingReturn] = useState<InventoryReturn | null>(null)
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([])
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [consumptions, setConsumptions] = useState<InventoryConsumption[]>([])
  const [expenseArticles, setExpenseArticles] = useState<ExpenseArticle[]>([])
  const [tournaments, setTournaments] = useState<Tournament[]>([])
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    warehouseId: '',
    basisId: '',
    tournamentId: '',
    status: 'POSTED',
    comment: ''
  })
  
  const [items, setItems] = useState<InventoryReturnItem[]>([])
  const [openedViaQuery, setOpenedViaQuery] = useState(false)
  const [showJsonModal, setShowJsonModal] = useState(false)
  const [selectedJsonData, setSelectedJsonData] = useState<any>(null)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [warehouseFilter, setWarehouseFilter] = useState('')
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  })

  // Загрузка данных
  const fetchData = useCallback(async (page = pagination.page) => {
    setLoading(true)
    try {
      const apiParams: Record<string, string | number> = {
        page: page,
        limit: pagination.limit
      }
      
      if (dateFrom) apiParams.from = dateFrom
      if (dateTo) apiParams.to = dateTo
      if (warehouseFilter) apiParams.warehouseId = warehouseFilter

      const [returnsData, itemsData, warehousesData, consumptionsData, articlesData, tournamentsData] = await Promise.all([
        apiGet<{ returns?: InventoryReturn[]; pagination?: { page?: number; total?: number; totalPages?: number } }>('/inventory-returns', apiParams),
        apiGet<{ items?: InventoryItem[] }>('/inventory-items'),
        apiGet<Warehouse[]>('/warehouses'),
        apiGet<{ consumptions?: InventoryConsumption[] }>('/inventory-consumptions?limit=1000'),
        apiGet<ExpenseArticle[] | { articles?: ExpenseArticle[] }>('/expense-articles'),
        apiGet<{ tournaments?: Tournament[] } | Tournament[]>('/tournaments?page=1&limit=1000')
      ])
      
      setReturns(returnsData.returns || [])
      setPagination(prev => ({
        ...prev,
        page: returnsData.pagination?.page || page,
        total: returnsData.pagination?.total || 0,
        totalPages: returnsData.pagination?.totalPages || 0
      }))
      setInventoryItems(Array.isArray(itemsData) ? [] : (itemsData.items || []))
      setWarehouses(Array.isArray(warehousesData) ? warehousesData : [])
      setConsumptions(Array.isArray(consumptionsData) ? [] : (consumptionsData.consumptions || []))
      console.log('Loaded consumptions:', Array.isArray(consumptionsData) ? [] : (consumptionsData.consumptions || []))
      console.log('Total consumptions loaded:', Array.isArray(consumptionsData) ? 0 : (consumptionsData.consumptions || []).length)
      setExpenseArticles(Array.isArray(articlesData) ? articlesData : (articlesData.articles || []))
      const tournaments = Array.isArray(tournamentsData) ? tournamentsData : (tournamentsData.tournaments || [])
      const sortedTournaments = [...tournaments].sort((a, b) => {
        const statusA = String(a.status || '').toUpperCase()
        const statusB = String(b.status || '').toUpperCase()
        const priorityA = TOURNAMENT_STATUS_PRIORITY[statusA] ?? Number.MAX_SAFE_INTEGER
        const priorityB = TOURNAMENT_STATUS_PRIORITY[statusB] ?? Number.MAX_SAFE_INTEGER
        if (priorityA !== priorityB) return priorityA - priorityB
        const endTsA = a.endDate ? new Date(a.endDate).getTime() : 0
        const endTsB = b.endDate ? new Date(b.endDate).getTime() : 0
        if (endTsA !== endTsB) return endTsB - endTsA
        return String(a.name || '').localeCompare(String(b.name || ''), 'ru')
      })
      setTournaments(sortedTournaments)
    } catch (error) {
      console.error('Ошибка загрузки данных:', error)
    } finally {
      setLoading(false)
    }
  }, [dateFrom, dateTo, warehouseFilter])

  // Загружаем сохраненные фильтры из localStorage
  useEffect(() => {
    const savedDateFrom = localStorage.getItem('inventoryReturns_dateFrom')
    const savedDateTo = localStorage.getItem('inventoryReturns_dateTo')
    const savedWarehouseFilter = localStorage.getItem('inventoryReturns_warehouseFilter')
    
    if (savedDateFrom) {
      setDateFrom(savedDateFrom)
    }
    if (savedDateTo) {
      setDateTo(savedDateTo)
    }
    if (savedWarehouseFilter) {
      setWarehouseFilter(savedWarehouseFilter)
    }
  }, [])

  // Загружаем данные только при монтировании компонента
  useEffect(() => {
    // Небольшая задержка, чтобы даты успели установиться из localStorage
    const timer = setTimeout(() => {
      fetchData(1)
    }, 100)
    
    return () => clearTimeout(timer)
  }, [])

  // Сбрасываем на первую страницу при изменении фильтров
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }))
    fetchData(1)
  }, [dateFrom, dateTo, warehouseFilter])

  // Функции для работы с фильтрами по датам
  const handleDateFromChange = (value: string) => {
    setDateFrom(value)
    localStorage.setItem('inventoryReturns_dateFrom', value)
    // Не вызываем fetchData автоматически - только при нажатии "Обновить"
  }

  const handleDateToChange = (value: string) => {
    setDateTo(value)
    localStorage.setItem('inventoryReturns_dateTo', value)
    // Не вызываем fetchData автоматически - только при нажатии "Обновить"
  }

  const handleWarehouseFilterChange = (value: string) => {
    setWarehouseFilter(value)
    localStorage.setItem('inventoryReturns_warehouseFilter', value)
    // Не вызываем fetchData автоматически - только при нажатии "Обновить"
  }

  const handleResetFilters = () => {
    setDateFrom('')
    setDateTo('')
    setWarehouseFilter('')
    localStorage.removeItem('inventoryReturns_dateFrom')
    localStorage.removeItem('inventoryReturns_dateTo')
    localStorage.removeItem('inventoryReturns_warehouseFilter')
    fetchData(1)
  }

  const handleRefresh = () => {
    fetchData(pagination.page)
  }

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchData(newPage)
    }
  }

  // Открытие на редактирование по query ?open=<id> или создание на основании ?createFrom=<id>
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const openId = params.get('open')
    const createFromId = params.get('createFrom')
    
    console.log('URL params:', { openId, createFromId })
    
    if (openId) {
      setOpenedViaQuery(true)
      ;(async () => {
        try {
          const doc = await apiGet<InventoryReturn>(`/inventory-returns/${openId}`)
          handleEdit(doc)
        } catch (e) {}
      })()
    } else if (createFromId) {
      console.log('Creating return from consumption ID:', createFromId)
      setOpenedViaQuery(true)
      handleCreateFromConsumption(createFromId)
    }
  }, [])

  // Обработчики формы
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.warehouseId) {
      alert('Выберите склад')
      return
    }
    
    if (items.length === 0) {
      alert('Добавьте хотя бы одну позицию')
      return
    }
    
    // Проверяем, что все позиции заполнены
    for (const item of items) {
      if (!item.inventoryItemId || !item.quantity || item.quantity <= 0 || !item.costPrice || item.costPrice <= 0) {
        alert('Заполните все позиции корректно')
        return
      }
    }
    
    try {
      const requestBody: any = {
        ...formData
      }
      
      // При создании нового документа всегда передаем позиции
      // При обновлении передаем позиции только если они были изменены
      if (!editingReturn || items.length > 0) {
        requestBody.items = items.map(item => ({
          inventoryItemId: item.inventoryItemId,
          ...(item.incomeArticleId && { incomeArticleId: item.incomeArticleId }),
          quantity: item.quantity,
          unit: item.unit || 'шт',
          costPrice: item.costPrice,
          basisItemId: item.basisItemId,
          comment: item.comment || null
        }))
      }

      if (editingReturn) {
        await apiPut(`/inventory-returns/${editingReturn.id}`, requestBody)
      } else {
        await apiPost('/inventory-returns', requestBody)
      }
      
      await fetchData()
      handleFormClose()
    } catch (error: any) {
      console.error('Ошибка сохранения:', error)
      alert(error.message || 'Ошибка сохранения документа')
    }
  }

  const handleFormClose = () => {
    setShowForm(false)
    setEditingReturn(null)
    setFormData({
      date: new Date().toISOString().split('T')[0],
      warehouseId: '',
      basisId: '',
      status: 'POSTED',
      comment: ''
    })
    setItems([])
    // Если документ открыт по ссылке из отчета (?open=...), возвращаемся по return или history.back()
    if (openedViaQuery) {
      const params = new URLSearchParams(window.location.search)
      const ret = params.get('return')
      if (ret) {
        window.location.href = decodeURIComponent(ret)
        return
      }
      if (params.get('open')) {
        window.history.back()
        return
      }
    }
  }

  const handleEdit = (returnDoc: InventoryReturn) => {
    setEditingReturn(returnDoc)
    setFormData({
      date: returnDoc.date.split('T')[0],
      warehouseId: returnDoc.warehouseId,
      basisId: returnDoc.basisId || '',
      tournamentId: returnDoc.tournamentId || '',
      status: returnDoc.status || 'DRAFT',
      comment: returnDoc.comment || ''
    })
    setItems(returnDoc.items?.map(item => ({
      inventoryItemId: item.inventoryItemId,
      incomeArticleId: item.incomeArticleId || '',
      quantity: item.quantity,
      unit: item.unit || 'шт',
      costPrice: item.costPrice,
      basisItemId: item.basisItemId,
      comment: item.comment || '',
      inventoryItem: item.inventoryItem,
      incomeArticle: item.incomeArticle
    })) || [])
    setShowForm(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Удалить документ? Это действие нельзя отменить.')) return
    
    try {
      await apiDelete(`/inventory-returns/${id}`)
      await fetchData()
    } catch (error: any) {
      console.error('Ошибка удаления:', error)
      alert(error.message || 'Ошибка удаления документа')
    }
  }

  const handleShowJson = (returnDoc: InventoryReturn) => {
    setSelectedJsonData(returnDoc)
    setShowJsonModal(true)
  }

  // Создание возврата на основании расхода
  const handleCreateFromConsumption = async (consumptionId: string) => {
    try {
      console.log('Creating return from consumption:', consumptionId)
      const data = await apiGet<{ consumption: InventoryConsumption; suggestedItems: any[] }>(`/inventory-returns/create-from/${consumptionId}`)
      const { consumption, suggestedItems } = data
      
      console.log('Consumption data:', consumption)
      console.log('Suggested items:', suggestedItems)
      
      // Устанавливаем дату документа равной дате расхода
      const consumptionDate = new Date(consumption.date).toISOString().split('T')[0]
      
      const newFormData = {
        date: consumptionDate,
        warehouseId: consumption.warehouseId,
        basisId: consumption.id,
        tournamentId: consumption.tournamentId || '',
        status: 'POSTED',
        comment: `Возврат на основании расхода ${consumption.number || consumption.id}`
      }
      
      console.log('Setting form data:', newFormData)
      setFormData(newFormData)
      
      setItems(suggestedItems.map((item: any) => ({
        inventoryItemId: item.returnItemId || item.inventoryItemId, // Используем returnItemId если есть
        incomeArticleId: getDefaultIncomeArticleId(), // Статья дохода по умолчанию SAL001
        quantity: Math.round(item.quantity), // Округляем до целых
        unit: 'шт',
        costPrice: item.costPrice,
        basisItemId: item.basisItemId,
        comment: '',
        inventoryItem: item.inventoryItem // Используем inventoryItem из suggestedItems
      })))
      
      setShowForm(true)
    } catch (error) {
      console.error('Ошибка создания возврата:', error)
      alert(error.message || 'Ошибка создания возврата')
    }
  }

  // Управление позициями
  const getDefaultIncomeArticleId = (): string => {
    const article = expenseArticles.find(
      (a) =>
        a.type === 'INCOME' &&
        String(a.code || '')
          .trim()
          .toUpperCase()
          .startsWith('SAL001')
    )
    return article?.id || ''
  }

  const addItem = () => {
    setItems([
      ...items,
      {
        inventoryItemId: '',
        incomeArticleId: getDefaultIncomeArticleId(),
        quantity: 0,
        unit: 'шт',
        costPrice: 0,
        comment: ''
      }
    ])
  }

  useEffect(() => {
    const defaultIncomeArticleId = getDefaultIncomeArticleId()
    if (!defaultIncomeArticleId) return

    setItems((prev) => {
      let changed = false
      const next = prev.map((item) => {
        if (item.incomeArticleId) return item
        changed = true
        return { ...item, incomeArticleId: defaultIncomeArticleId }
      })
      return changed ? next : prev
    })
  }, [expenseArticles])

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index))
  }

  const updateItem = (index: number, field: keyof InventoryReturnItem, value: any) => {
    const newItems = [...items]
    newItems[index] = { ...newItems[index], [field]: value }
    setItems(newItems)
  }

  const getInventoryItem = (id: string) => {
    return inventoryItems.find(item => item.id === id)
  }

  const getReturnItem = (id: string) => {
    return inventoryItems.find(item => item.id === id)
  }

  const getConsumption = (id: string) => {
    return consumptions.find(consumption => consumption.id === id)
  }

  const groupedTournaments = tournaments.reduce<Record<string, Tournament[]>>((acc, tournament) => {
    const status = String(tournament.status || '').toUpperCase()
    if (!acc[status]) acc[status] = []
    acc[status].push(tournament)
    return acc
  }, {})

  const orderedTournamentStatuses = Object.keys(groupedTournaments).sort((a, b) => {
    const pa = TOURNAMENT_STATUS_PRIORITY[a] ?? Number.MAX_SAFE_INTEGER
    const pb = TOURNAMENT_STATUS_PRIORITY[b] ?? Number.MAX_SAFE_INTEGER
    return pa - pb
  })

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Возврат ТМЦ</h1>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/purchases')}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            📦 Закупка ТМЦ
          </button>
          <button
            onClick={() => navigate('/inventory-consumptions')}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            📦 Расход ТМЦ
          </button>
          <button
            onClick={() => navigate('/inventory-sales')}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            💰 Продажа ТМЦ
          </button>
          <button
            onClick={() => navigate('/inventory-transfers')}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            🔄 Перемещение ТМЦ
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Создать документ
          </button>
        </div>
      </div>

      {/* Фильтры по датам и складу */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Дата с
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => handleDateFromChange(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Для навигации по месяцам используйте клавиши ← → или кликайте по датам"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Дата по
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => handleDateToChange(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Для навигации по месяцам используйте клавиши ← → или кликайте по датам"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Склад
            </label>
            <select
              value={warehouseFilter}
              onChange={(e) => handleWarehouseFilterChange(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Все склады</option>
              {warehouses.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={handleResetFilters}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
            >
              Сбросить
            </button>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
            >
              Обновить
            </button>
          </div>
        </div>
      </div>

      {/* Список документов */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Дата
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Основание
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Турнир
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Статус
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Комментарий
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Действия
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  Загрузка...
                </td>
              </tr>
            ) : returns.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  Нет документов
                </td>
              </tr>
            ) : (
              returns.map((returnDoc) => (
                <tr key={returnDoc.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(returnDoc.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {returnDoc.basisId 
                      ? (returnDoc.basis?.number || `Расход ${returnDoc.basisId.slice(-8)}`)
                      : '-'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {returnDoc.tournamentId 
                      ? (tournaments.find(t => t.id === returnDoc.tournamentId)?.name || 'Неизвестный турнир')
                      : '-'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      returnDoc.status === 'POSTED' ? 'bg-green-100 text-green-800' :
                      returnDoc.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {returnDoc.status === 'POSTED' ? 'Проведен' :
                       returnDoc.status === 'CANCELLED' ? 'Отменен' : 'Черновик'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {returnDoc.comment || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEdit(returnDoc)}
                      className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                      title="Редактировать"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleShowJson(returnDoc)}
                      className="text-gray-600 hover:text-gray-900 p-1 rounded hover:bg-gray-50"
                      title="Показать JSON"
                    >
                      📄
                    </button>
                    <button
                      onClick={() => handleDelete(returnDoc.id)}
                      className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                      title="Удалить"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Пагинация */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-center items-center space-x-2 mt-8">
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Назад
          </button>
          
          <div className="flex space-x-1">
            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
              const pageNum = Math.max(1, pagination.page - 2) + i;
              if (pageNum > pagination.totalPages) return null;
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    pageNum === pagination.page
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Вперед
          </button>
        </div>
      )}

      {/* Информация о пагинации */}
      {pagination.total > 0 && (
        <div className="text-center text-sm text-gray-500 mt-4">
          Показано {returns.length} из {pagination.total} документов
        </div>
      )}

      {/* Модальное окно формы */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingReturn ? 'Редактировать документ' : 'Создать документ возврата ТМЦ'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Заголовок документа */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Дата *
                    </label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Склад *
                    </label>
                    <select
                      value={formData.warehouseId}
                      onChange={(e) => setFormData({ ...formData, warehouseId: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="">Выберите склад</option>
                      {warehouses.map(warehouse => (
                        <option key={warehouse.id} value={warehouse.id}>
                          {warehouse.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Основание (расход ТМЦ)
                    </label>
                    <select
                      value={formData.basisId}
                      onChange={(e) => {
                        const selectedBasisId = e.target.value
                        const selectedConsumption = consumptions.find(c => c.id === selectedBasisId)
                        console.log('Basis changed:', { selectedBasisId, selectedConsumption, consumptions })
                        setFormData({ 
                          ...formData, 
                          basisId: selectedBasisId,
                          tournamentId: selectedConsumption?.tournamentId || ''
                        })
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">Без основания</option>
                      {consumptions
                        .filter(consumption => {
                          // Показываем расходы за последние 6 месяцев
                          const consumptionDate = new Date(consumption.date)
                          const sixMonthsAgo = new Date()
                          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)
                          return consumptionDate >= sixMonthsAgo
                        })
                        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) // Сортируем по дате (новые сверху)
                        .map(consumption => (
                          <option key={consumption.id} value={consumption.id}>
                            {consumption.number || consumption.id} - {consumption.warehouse?.name} ({formatDate(consumption.date)})
                          </option>
                        ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Турнир
                    </label>
                    <select
                      value={formData.tournamentId}
                      onChange={(e) => setFormData({ ...formData, tournamentId: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">Без турнира</option>
                      {orderedTournamentStatuses.map((status) => (
                        <optgroup
                          key={status}
                          label={`──────── ${TOURNAMENT_STATUS_LABELS[status] || status} ────────`}
                        >
                          {(groupedTournaments[status] || []).map((tournament) => (
                            <option key={tournament.id} value={tournament.id}>
                              {tournament.name}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Статус
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="DRAFT">Черновик</option>
                      <option value="POSTED">Проведен</option>
                      <option value="CANCELLED">Отменен</option>
                    </select>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Комментарий
                    </label>
                    <input
                      type="text"
                      value={formData.comment}
                      onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="Опциональный комментарий"
                    />
                  </div>
                </div>

                {/* Табличная часть */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Позиции документа *
                    </label>
                    <button
                      type="button"
                      onClick={addItem}
                      className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                    >
                      Добавить позицию
                    </button>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="min-w-full border border-gray-300">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            ТМЦ
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Количество
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Единица
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Цена за единицу
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Статья дохода
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Основание
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Комментарий
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Действия
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => {
                          const inventoryItem = getInventoryItem(item.inventoryItemId)
                          return (
                            <tr key={index} className="border-t">
                              <td className="px-3 py-2">
                                <select
                                  value={item.inventoryItemId}
                                  onChange={(e) => updateItem(index, 'inventoryItemId', e.target.value)}
                                  className="w-full border border-gray-300 rounded px-2 py-1"
                                  required
                                >
                                  <option value="">Выберите ТМЦ</option>
                                  {inventoryItems.map(invItem => (
                                    <option key={invItem.id} value={invItem.id}>
                                      {invItem.name} ({invItem.unit})
                                    </option>
                                  ))}
                                </select>
                                {item.inventoryItem && (
                                  <div className="text-xs text-gray-600 mt-1">
                                    Выбрано: {item.inventoryItem.name}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.quantity}
                                  onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                                  className="w-full border border-gray-300 rounded px-2 py-1"
                                  required
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="text"
                                  value={item.unit || 'шт'}
                                  onChange={(e) => updateItem(index, 'unit', e.target.value)}
                                  className="w-full border border-gray-300 rounded px-2 py-1"
                                  placeholder="шт"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.costPrice}
                                  onChange={(e) => updateItem(index, 'costPrice', parseFloat(e.target.value) || 0)}
                                  className="w-full border border-gray-300 rounded px-2 py-1"
                                  required
                                />
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  value={item.incomeArticleId || ''}
                                  onChange={(e) => updateItem(index, 'incomeArticleId', e.target.value)}
                                  className="w-full border border-gray-300 rounded px-2 py-1"
                                >
                                  <option value="">Выберите статью</option>
                                  {expenseArticles
                                    .filter(article => article.type === 'INCOME' && article.isActive)
                                    .map(article => (
                                      <option key={article.id} value={article.id}>
                                        {article.code} - {article.name}
                                      </option>
                                    ))}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  value={item.basisItemId || ''}
                                  onChange={(e) => updateItem(index, 'basisItemId', e.target.value)}
                                  className="w-full border border-gray-300 rounded px-2 py-1"
                                >
                                  <option value="">Без основания</option>
                                  {formData.basisId && getConsumption(formData.basisId)?.items?.map(basisItem => (
                                    <option key={basisItem.id} value={basisItem.id}>
                                      {basisItem.inventoryItem?.name} - {basisItem.quantity} {basisItem.unit}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="text"
                                  value={item.comment || ''}
                                  onChange={(e) => updateItem(index, 'comment', e.target.value)}
                                  className="w-full border border-gray-300 rounded px-2 py-1"
                                  placeholder="Состояние/сорт"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <button
                                  type="button"
                                  onClick={() => removeItem(index)}
                                  className="text-red-600 hover:text-red-800 text-sm"
                                >
                                  Удалить
                                </button>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Кнопки */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleFormClose}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editingReturn ? 'Обновить' : 'Создать'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для отображения JSON */}
      {showJsonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[80vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                JSON содержимое документа
              </h3>
              <button
                onClick={() => setShowJsonModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="bg-gray-100 p-4 rounded-lg">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap overflow-auto">
                {JSON.stringify(selectedJsonData, null, 2)}
              </pre>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowJsonModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default InventoryReturns
